/**
 * Fachbereich-Gruppen-Pflichtfeld (TES-1867/TES-1868) — geteiltes Feld der
 * Zuweisungs-Sektionen (u.a. EditInspectionDialog, Fallow-Dedup TES-1998).
 *
 * TES-2047: intern auf den extended GroupPicker (TES-1878) umgestellt — Standort-
 * FilterPills + Attribut-Suche (standort/group_type/owner/department/team) statt
 * simplem Select-Dropdown, konsistent zu DefectReportDialog/InspectionPointAssign/
 * MppChecklistAssignmentPanel. Die Props-API ist NACH AUSSEN unverändert
 * (value = Gruppen-ID, "" = keine); der Parent resettet seinen Bearbeiter beim
 * Gruppenwechsel selbst (eigene State-Setter).
 *
 * Testid-/Anker-Migration (TES-2047, dokumentiert): `id` + `testId` markieren jetzt
 * den Feld-CONTAINER; der klickbare Trigger darin ist die AssignmentCell
 * (`assignment-cell` / `assignment-cell-empty`, @plasser/ui) — E2E klickt
 * `#<id> [data-testid="assignment-cell(-empty)"]` (vgl. tes-l6-inspection-group-assign).
 */
import * as React from "react";
import { AssignmentCell, LayeredModalHost, useFlowSurface, type EntityRecord } from "@plasser/ui";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/hooks/useAuth";
import { GroupPicker } from "@/components/ui/pickers";

interface AssignedGroupFieldProps {
  /** id des Feld-Containers (E2E-Anker, vormals Select-Trigger). */
  id: string;
  /** Fertiger i18n-Label-Text (Pflicht-Stern rendert das Feld selbst). */
  label: string;
  /** Fertiger i18n-Platzhalter für den leeren Zustand. */
  placeholder: string;
  /** Aktuelle Gruppen-ID ("" = keine). */
  value: string;
  /** Bekannte Gruppen (Name-Auflösung der aktuellen ID); die Suche läuft über den Picker. */
  groups: { id: string; name: string }[];
  /** Nächste Gruppen-ID ("" = keine) — Parent setzt State + resettet Bearbeiter. */
  onChange: (nextGroupId: string) => void;
  disabled?: boolean;
  testId?: string;
}

export function AssignedGroupField(props: AssignedGroupFieldProps) {
  // useFlowSurface braucht einen LayeredModalHost-Vorfahren (vgl. InspectionPointsAssignment/
  // MppPlanAssignment). Damit ALLE Aufrufer unverändert bleiben, bringt das Feld seinen Host
  // selbst mit — die Picker-Overlays portalen ohnehin nach document.body.
  return (
    <LayeredModalHost>
      <AssignedGroupFieldBody {...props} />
    </LayeredModalHost>
  );
}

/**
 * Anzeigename der aktuellen Gruppen-ID — flacher Helper (Muster TES-2028): hält die
 * Render-Funktion unter den fallow-CRAP-Schwellen, wenn der CI-Coverage-Step timeoutet
 * und fallow ohne Coverage scort. Fallback-Kette: bekannte Gruppe → frisch gepickter
 * Name (Picker sucht tenant-weit, groups ist team-gefiltert) → rohe ID.
 */
function resolveGroupName(
  groups: { id: string; name: string }[],
  value: string,
  pickedName: string | null,
): string {
  const known = groups.find((g) => g.id === value);
  return known?.name ?? pickedName ?? value;
}

/** Provider-Kontext des GroupPickers mit Null-Fallbacks — flach ausgelagert (s.o.). */
function toPickerProviderContext(
  user: { id: string } | null,
  profile: { active_team_id: string | null } | null,
): { userId: string | null; activeTeamId: string | null } {
  return {
    userId: user ? user.id : null,
    activeTeamId: profile ? profile.active_team_id : null,
  };
}

function AssignedGroupFieldBody({
  id,
  label,
  placeholder,
  value,
  groups,
  onChange,
  disabled,
  testId,
}: AssignedGroupFieldProps) {
  const { user, profile } = useAuth();
  const flow = useFlowSurface("medium", { title: label });

  // Der Picker sucht tenant-weit, die groups-Liste ist (noch) team-gefiltert — den Namen einer
  // frisch gepickten, dort unbekannten Gruppe lokal merken, damit der Chip nicht die rohe ID zeigt.
  const [pickedName, setPickedName] = React.useState<string | null>(null);
  const current = value ? { id: value, name: resolveGroupName(groups, value, pickedName) } : null;
  const providerContext = toPickerProviderContext(user, profile);

  const pick = async () => {
    const record = await flow.open<EntityRecord>(
      /* istanbul ignore next -- GroupPicker-Glue rendert nur im echten Layered Modal; e2e-gedeckt (R-SYM-04, tes-l6-inspection-group-assign.spec.ts) */
      (api) => (
        <div className="h-[70vh]">
          <GroupPicker
            providerContext={providerContext}
            autoFocus
            onConfirm={(sel) => api.close(sel[0])}
          />
        </div>
      ),
    );
    if (record) {
      setPickedName(record.name);
      onChange(record.id);
    }
  };

  return (
    <div className="space-y-2" id={id} data-testid={testId}>
      <Label className="flex items-center gap-1">
        {label}
        <span className="text-destructive">*</span>
      </Label>
      <AssignmentCell
        value={current}
        onAssign={pick}
        // AssignmentCell unterdrückt das Entfernen selbst, solange disabled gesetzt ist.
        onClear={value ? () => onChange("") : undefined}
        placeholder={placeholder}
        disabled={disabled}
      />
    </div>
  );
}
